import { Alert, Button, Tag } from 'antd';
import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { QuoteDecisionModal } from './QuoteDecisionModal';
import type { JobDetail } from '../../../jobs.api';
import type { useJobQuote } from '../hooks/use-job-quote';

interface JobQuotePanelProps {
  job: JobDetail;
  quote: ReturnType<typeof useJobQuote>;
}

/**
 * Shown on a job's own page whenever it's quoted (gitaumoses4/chukua#34): the frozen amount and
 * validity, an "Expired" tag once `job.quote.expired` — a read-only flag, the quote stays fully
 * actionable past its date — and, only for whoever holds `job.create`, Accept and Reject.
 * Button-hiding is UX only; the route's permission guard remains the real authority, same as
 * `JobCreditHoldPanel`'s `canManage`.
 */
export function JobQuotePanel({ job, quote }: JobQuotePanelProps) {
  const [decision, setDecision] = useState<'accept' | 'reject' | null>(null);
  const { state } = useCurrentUser();

  if (job.status !== 'quoted' || !job.quote) return null;

  const canDecide = state.status === 'authenticated' && state.user.permissions.includes('job.create');

  return (
    <>
      <Alert
        className="mb-5"
        type={job.quote.expired ? 'warning' : 'info'}
        showIcon
        message={
          <div className="flex flex-wrap items-center gap-3">
            <Tag color="processing">Quoted</Tag>
            <span>
              {formatMoney(job.quote.amount)} · valid until {job.quote.validUntil}
            </span>
            {job.quote.expired && <Tag color="error">Expired</Tag>}
            {canDecide && (
              <div className="ml-auto flex gap-2">
                <Button size="small" loading={quote.submitting} onClick={() => setDecision('reject')}>
                  Reject
                </Button>
                <Button size="small" type="primary" loading={quote.submitting} onClick={() => setDecision('accept')}>
                  Accept
                </Button>
              </div>
            )}
          </div>
        }
      />
      <QuoteDecisionModal
        decision={decision}
        jobNumber={job.number}
        onCancel={() => setDecision(null)}
        onAccept={async (input) => {
          await quote.accept(job, input);
          setDecision(null);
        }}
        onReject={async (reason) => {
          await quote.reject(job, reason);
          setDecision(null);
        }}
      />
    </>
  );
}
